import React, { FC, useMemo } from 'react';
import hoistNonReactStatics from 'hoist-non-react-statics';

import getWrappedDisplayName from '../../../utils/hocs/getWrappedDisplayName';
import useSubscriptionToChanges from '../../../hooks/_useSubscriptionToChanges';

const connectScoped = <
  Props extends {},
  Deps extends unknown[]
>(
    keyMap: Record<string, string> | ((...deps: Deps) => Record<string, string>),
    mapPropsToDependencies: (props: Props) => Deps = () => []as Deps,
    options: ((...deps: Deps) => { scope?: string[] }) | { scope?: string[] } = {},
  ) => (WrappedComponent: FC<Props>) => {
    const ConnectScoped = (props: Props) => {
      const dependencies = useMemo(() => mapPropsToDependencies(props), [props]);

      const resolvedKeyMap = useMemo(() => (
        (typeof keyMap === 'function') ? keyMap(...dependencies) : keyMap
      ), [dependencies]);

      const resolvedOptions = useMemo(() => (
        (typeof options === 'function') ? options(...dependencies) : options
      ), [dependencies]);

      const mappedProps = useSubscriptionToChanges(resolvedKeyMap, resolvedOptions);

      return <WrappedComponent {...props} {...mappedProps} />;
    };

    ConnectScoped.displayName = getWrappedDisplayName('ConnectScoped', WrappedComponent);

    return hoistNonReactStatics(ConnectScoped, WrappedComponent);
  };

export default connectScoped;
